import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuditCatalogue } from '../context/AuditCatalogueContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { landingRoute } from '../navigation';
import { ApiError } from '../services/api';
import { auditApi } from '../services/auditApi';
import { AuditActor, AuditEntry, AuditFilter } from '../types/audit';
import AuditFilters, { filterErrors } from '../components/audit/AuditFilters';
import AuditTable from '../components/audit/AuditTable';
import AuditDetailDrawer from '../components/audit/AuditDetailDrawer';
import CursorPagination from '../components/CursorPagination';
import { dubaiDay, dubaiDaysAgo } from '../components/audit/auditFormat';

/** URL ⇄ filter. Absent dates mean the default window (the last 7 Dubai days, today included). */
export function filterFromParams(params: URLSearchParams, now: Date = new Date()): AuditFilter {
  const actions = params.getAll('action').flatMap((a) => a.split(',')).map((a) => a.trim()).filter(Boolean);
  return {
    from: params.get('from') || dubaiDaysAgo(6, now),
    to: params.get('to') || dubaiDay(now),
    adminSubjectId: params.get('admin') || undefined,
    actions: actions.length ? actions : undefined,
    entityType: params.get('entityType') || undefined,
    garageId: params.get('garageId') || undefined,
    q: params.get('q') || undefined,
  };
}

export function paramsFromFilter(filter: AuditFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  if (filter.adminSubjectId) p.set('admin', filter.adminSubjectId);
  (filter.actions ?? []).forEach((a) => p.append('action', a));
  if (filter.entityType) p.set('entityType', filter.entityType);
  if (filter.garageId) p.set('garageId', filter.garageId);
  if (filter.q) p.set('q', filter.q);
  return p;
}

/**
 * Spec 069 Flow B — the unified admin audit: filters (mirrored in the URL), newest-first rows with
 * "Load more", a detail drawer and CSV export. Responses to an older filter are dropped (request token).
 */
export default function AuditPage() {
  const auth = useAuth();
  const features = useFeatureFlags();
  const navigate = useNavigate();
  const { entityTypes } = useAuditCatalogue();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = searchParams.toString();
  const filter = useMemo(() => filterFromParams(new URLSearchParams(filterKey)), [filterKey]);
  const errors = filterErrors(filter);
  const blocked = !!(errors.range || errors.garage);

  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actors, setActors] = useState<AuditActor[]>([]);
  const [actorsError, setActorsError] = useState<string | null>(null);
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const requestToken = useRef(0);

  // Latest access + switches, read when leaving: keeps `load` stable so only a filter change refetches.
  const access = useRef({ auth, features });
  access.current = { auth, features };
  const leaveIfDisabled = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 404 && err.code === 'FEATURE_DISABLED') {
      const { auth: a, features: f } = access.current;
      navigate(landingRoute(a.isSuperAdmin, a.hasScreen, { ...f, auditScreen: false }) ?? '/', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const load = useCallback(async (cursor?: string) => {
    if (!auth.token) return;
    const token = ++requestToken.current;
    if (cursor) setLoadingMore(true); else { setLoading(true); setError(null); }
    try {
      const page = await auditApi.list(filter, cursor, auth.token);
      if (token !== requestToken.current) return; // a newer filter won: drop this response
      setRows((prev) => (cursor ? [...prev, ...page.content] : page.content));
      setNextCursor(page.nextCursor ?? undefined);
      setHasMore(page.hasMore);
    } catch (err) {
      if (token !== requestToken.current) return;
      if (leaveIfDisabled(err)) return;
      setError(err instanceof Error ? err.message : 'Could not load admin actions');
      if (!cursor) { setRows([]); setHasMore(false); }
    } finally {
      if (token === requestToken.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [auth.token, filter, leaveIfDisabled]);

  useEffect(() => {
    if (blocked) {
      requestToken.current++; // an invalid filter sends nothing and discards anything in flight
      setLoading(false);
      return;
    }
    load();
  }, [load, blocked]);

  useEffect(() => {
    if (!auth.token) return;
    auditApi.actors(auth.token)
      .then(setActors)
      .catch((err) => { if (!leaveIfDisabled(err)) setActorsError(err instanceof Error ? err.message : 'unavailable'); });
  }, [auth.token, leaveIfDisabled]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const applyFilter = (next: AuditFilter) => {
    setExportError(null);
    setSearchParams(paramsFromFilter(next));
  };

  const lastThirtyDays = () => applyFilter({ ...filter, from: dubaiDaysAgo(29), to: dubaiDay() });

  const doExport = async () => {
    if (!auth.token || exporting || blocked) return;
    setExporting(true);
    setExportError(null);
    try {
      const name = await auditApi.exportCsv(filter, auth.token);
      setToast(`Downloaded ${name}`);
    } catch (err) {
      if (leaveIfDisabled(err)) return;
      if (err instanceof ApiError && (err.status === 422 || err.code === 'EXPORT_TOO_LARGE')) {
        setExportError('Narrow the filter: export is limited to 10,000 rows.');
      } else {
        setExportError(err instanceof Error ? err.message : 'Export failed');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="audit-page">
      <div className="audit-page__head">
        <h2 className="audit-page__title">Audit</h2>
        <div className="audit-page__actions">
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={doExport}
            disabled={exporting || blocked}
            aria-busy={exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>
      {exportError && <p className="audit-hint audit-hint--error" role="alert">{exportError}</p>}

      <div className="admin-card">
        <AuditFilters value={filter} onChange={applyFilter} actors={actors} actorsError={actorsError} entityTypes={entityTypes} />

        {error && (
          <div className="admin-info-banner danger audit-error" role="alert">
            {error} <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => load()}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : blocked ? null : rows.length === 0 && !error ? (
          <div className="admin-empty-state audit-empty">
            <p>No actions in this range.</p>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={lastThirtyDays}>Last 30 days</button>
          </div>
        ) : rows.length > 0 ? (
          <>
            <AuditTable rows={rows} onOpen={setOpen} />
            <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => load(nextCursor)} />
          </>
        ) : null}
      </div>

      <div className="sr-only" aria-live="polite">{toast ?? ''}</div>
      {toast && <div className="audit-toast" aria-hidden="true">{toast}</div>}

      {open && <AuditDetailDrawer row={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
